import React, {
  useContext,
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect,
} from 'react'

import { Box, Button, Modal, Typography } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { mkConfig, generateCsv, download } from 'export-to-csv'
import styles from './ExportActionsCustom.module.css'

// import { jsPDF } from 'jspdf' //or use your library of choice here
// import autoTable from 'jspdf-autotable'

import { observer } from 'mobx-react-lite'

import ErrorIcon from '@mui/icons-material/Error'
import CloseIcon from '@mui/icons-material/Close'

import { SvgValidate } from '../../../../components/svg/SvgComponents'

import { ViewFormContext } from '../../../ViewForm/components/store/store.ViewForm'
import { BuilderContext } from '../../../Sandbox/components/store/store.Builder'

import * as XLSX from 'xlsx'

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
})

const ExportActionsCustom = observer(({ table, data, closeModal }) => {
  const [exportFormat, setExportFormat] = useState('CSV')
  const [isExporting, setIsExporting] = useState(false)
  const [exportSuccess, setExportSuccess] = useState(false)
  const [exportError, setExportError] = useState(false)

  const viewFormContext = useContext(ViewFormContext)
  const builderContext = useContext(BuilderContext)
  const answersStore = builderContext.AnswersStore

  const getExportFunction = () => {
    return exportFormat === 'CSV' ? exportRowsAsCSV : exportRowsAsXLS
  }

  const isNumeric = (value) => {
    return !isNaN(value) && !isNaN(parseFloat(value))
  }

  const mapDataForExport = (rows, fromFullData = false) => {
    return rows.map((row) => {
      const rowData = fromFullData ? row : row.original
      let newRow = {}
      answersStore.headers.forEach((col) => {
        const cellValue = rowData[col.accessorKey]
        newRow[col.header] = isNumeric(cellValue)
          ? parseFloat(cellValue)
          : cellValue
      })
      return newRow
    })
  }

  const exportRowsAsCSV = (rows, fromFullData = false) => {
    const rowData = mapDataForExport(rows, fromFullData)
    const csv = generateCsv(csvConfig)(rowData)
    download(csvConfig)(csv)
  }

  const exportRowsAsXLS = (rows, fromFullData = false) => {
    const wb = XLSX.utils.book_new()
    const rowData = mapDataForExport(rows, fromFullData)
    const ws = XLSX.utils.json_to_sheet(rowData)
    XLSX.utils.book_append_sheet(wb, ws, 'Data')
    XLSX.writeFile(wb, 'data.xlsx')
  }

  // Fonction pour gérer l'exportation de toutes les données
  const handleExportAllData = async () => {
    setIsExporting(true)
    setExportSuccess(false)
    setExportError(false)

    try {
      const exportFunc = getExportFunction()
      await answersStore.exportAllData(exportFunc)

      setExportSuccess(true)
      closeModal()
    } catch (error) {
      console.error("Erreur lors de l'exportation : ", error)
      setExportError(true)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportRows = (rows) => {
    const exportFunc = getExportFunction()
    exportFunc(rows)
  }

  // Calculer si le bouton doit être désactivé
  const isExportSelectedRowsButtonDisabled = useMemo(() => {
    return !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
  }, [
    table.getIsSomeRowsSelected(),
    table.getIsAllRowsSelected(),
    answersStore.rowSelection,
  ])

  return (
    <div className={styles.modal_export}>
      <div>
        <div className={styles.title_category}>Format de l'exportation</div>
        <div className={styles.container_format_export_full}>
          <div className={styles.container_format_export}>
            <div
              className={`${styles.button__container} ${
                exportFormat === 'CSV' ? styles.active : ''
              }`}
              onClick={() => setExportFormat('CSV')}
            >
              CSV
            </div>
            <div
              className={`${styles.button__container} ${
                exportFormat === 'XLS' ? styles.active : ''
              }`}
              onClick={() => setExportFormat('XLS')}
            >
              EXCEL
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className={styles.title_category}>Données à exporter</div>

        <div className={styles.content_buttons}>
          <Button
            className={styles.a_button}
            onClick={handleExportAllData}
            startIcon={<FileDownloadIcon />}
          >
            Exporter toutes les données
          </Button>

          <Button
            className={styles.a_button}
            disabled={table.getRowModel().rows.length === 0}
            onClick={() => handleExportRows(table.getRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Exporter les données visibles
          </Button>

          <Button
            className={styles.a_button}
            disabled={isExportSelectedRowsButtonDisabled}
            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Exporter les données séléctionnées
          </Button>
        </div>
      </div>

      <Modal
        open={isExporting || exportSuccess || exportError}
        onClose={() => {
          setExportSuccess(false)
          setExportError(false)
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: '350px',
            minHeight: '350px',
            width: '50%',
            height: '50%',
            maxWidth: '650px',
            maxHeight: '650px',
            padding: 4,
            bgcolor: 'background.paper',
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isExporting && (
            <div className={styles.loading_content}>
              <div className={styles.logoname}>FORMA</div>
              <span className={styles.loader}></span>
            </div>
          )}
          {exportSuccess && (
            <>
              <div className={styles.content_bloc}>
                <div className={styles.content_valide}>
                  <div className={styles.content_svg_logo}>
                    <SvgValidate />
                  </div>
                </div>
                <div className={styles.content_bloc_title}>
                  Succès de l'exportation !
                </div>
              </div>
            </>
          )}
          {exportError && (
            <>
              <ErrorIcon color="error" />
              <Typography>Erreur lors de l'exportation.</Typography>
            </>
          )}
          {(exportSuccess || exportError) && (
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <Button
                onClick={() => {
                  setExportSuccess(false)
                  setExportError(false)
                }}
              >
                <CloseIcon />
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  )
})

export default ExportActionsCustom
